/**
 * useDemoMode
 * Sprint FF-V2: React hook for demo mode status with polling
 *
 * Polls GET /api/admin/demo-orgs/:orgId/status every 60 seconds to detect
 * auto-expiry or manual disable without requiring a page refresh.
 *
 * Created: February 27, 2026
 */

import { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "@shared/services/apiClient";
import { useAuth } from "@shared/context/AuthContext";

export interface DemoModeStatus {
  isDemo: boolean;
  reason?: string;
  expiresAt?: string;
  msRemaining?: number;
  sessionId?: string;
}

const POLL_INTERVAL_MS = 60_000; // 60 seconds

export function useDemoMode(orgId: string | null | undefined): DemoModeStatus & {
  disable: () => Promise<void>;
  loading: boolean;
  error: string | null;
} {
  const { user } = useAuth();
  const [status, setStatus] = useState<DemoModeStatus>({ isDemo: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const userRole = String(user?.role || "").toUpperCase();
  const canReadDemoMode = userRole === "SUPER_ADMIN";

  const fetchStatus = useCallback(async () => {
    if (!orgId || !canReadDemoMode) {
      setStatus({ isDemo: false });
      return;
    }

    try {
      const resp = await apiClient.get(`/admin/demo-orgs/${orgId}/status`);
      const data = resp.data?.data;

      if (data?.is_demo) {
        setStatus({
          isDemo: true,
          reason: data.reason,
          expiresAt: data.expires_at,
          msRemaining: data.ms_remaining,
          sessionId: data.session_id,
        });
      } else {
        setStatus({ isDemo: false });
      }
      setError(null);
    } catch (err: any) {
      // 403/404 means no access or no session — treat as not-demo
      const status = err?.response?.status;
      if (status === 404 || status === 403) {
        setStatus({ isDemo: false });
      } else {
        setError("Failed to check demo mode status");
      }
    }
  }, [canReadDemoMode, orgId]);

  useEffect(() => {
    if (!orgId || !canReadDemoMode) {
      setStatus({ isDemo: false });
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchStatus().finally(() => setLoading(false));

    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [canReadDemoMode, orgId, fetchStatus]);

  const disable = useCallback(async () => {
    if (!orgId || !canReadDemoMode) return;
    await apiClient.post(`/admin/demo-orgs/${orgId}/disable`);
    // Immediately re-fetch to update state
    await fetchStatus();
  }, [canReadDemoMode, orgId, fetchStatus]);

  return { ...status, disable, loading, error };
}
